'use client';

import React, {
  createContext,
  useContext,
  useMemo,
  useOptimistic,
  startTransition,
  useCallback,
  useRef
} from 'react';
import cartReducer from './cart-reducer';
import {
  removeFromCartAPI,
  addToCartAPI,
  updateCartAPI,
  createCartAPI,
  isBotDeniedError
} from './cart-api-utils';
import useShopifyCart from './use-shopify-cart';
import { useToast } from '@/components/toast/ToastProvider';
import AddToCartToastBody from '@/components/toast/toast-bodies/AddToCartToastBody';
import ErrorAddToCartToastBody from '@/components/toast/toast-bodies/ErrorAddToCartToastBody';
import { validateAndRefreshBundleDiscounts } from '@/lib/bundles/cart-bundle-validation';
import { useHydrated } from '@/lib/hooks/use-hydrated';

const CartContext = createContext(undefined);

const BOT_BLOCKED_WARNING = 'Blocked cart request (bot security check)';

/**
 * A promise plus the handle to resolve it later. Cart mutations do their work
 * inside `startTransition`, whose callback can't hand a value back out to the
 * any callers.
 */
function createDeferred() {
  let resolve;
  const promise = new Promise((res) => {
    resolve = res;
  });
  return { promise, resolve };
}

/**
 * This provider holds a copy of the optimistic cart as well as utility
 * functions for managing cart updates with context and the Shopify API.
 * - Uses startTransition to mark actions as non-blocking, and most importantly
 *   it lets React know not to double-render the optimistic cart.
 * - Updates the UI optimistically via updateOptimisticCart for a smooth user
 *   experience before the API call completes.
 * - Sends an API request to update the cart in Shopify, handling quantity
 *   changes and item deletions.
 * - Updates the cart state with the latest data from the API response. The
 *   optimistic cart is then replaced with the real cart.
 */
export function CartProvider({ children }) {
  const { addToast } = useToast();

  // The real cart from shopify, fed into optimistic cart when updated
  const [cart, setCart, cartReady, initialCartPromise] = useShopifyCart();

  // Optimistic cart is what is shown. Replaced with real cart when not pending
  const [optimisticCart, updateOptimisticCart] = useOptimistic(
    cart,
    cartReducer
  );

  // Carts are created lazily on the first add-to-cart rather than eagerly on
  // page load (see use-shopify-cart.js). This ref holds the in-flight
  // cart-creation promise so that if the user clicks "add to cart" more than
  // once before a cart exists (e.g. double click, or clicking a second
  // product quickly), the second call reuses the same cart instead of
  // creating a separate one and silently losing the first click's items.
  const cartCreationRef = useRef(null);

  const addOrCreateCart = useCallback(
    async (itemsToAdd) => {
      const initialCart = await initialCartPromise;
      const existingId = cart?.id ?? initialCart?.id;

      if (existingId) {
        return addToCartAPI(existingId, itemsToAdd);
      }

      if (cartCreationRef.current) {
        // A cart is already being created by another in-flight add-to-cart
        // call; wait for it, then add these items to that cart instead of
        // creating a second one.
        const createdCart = await cartCreationRef.current;
        return addToCartAPI(createdCart.id, itemsToAdd);
      }

      // No cart yet: create one pre-populated with these items in a single
      // request (Shopify's cartCreate mutation accepts lines directly), so
      // the very first add-to-cart of a session costs the same one
      // round-trip as a normal add rather than a create-then-add sequence.
      const creationPromise = createCartAPI(itemsToAdd);
      cartCreationRef.current = creationPromise;
      try {
        return await creationPromise;
      } catch (error) {
        // Keep successful creations so adds made before setCart publishes the
        // cart reuse it; clear failures so they can be retried.
        cartCreationRef.current = null;
        throw error;
      }
    },
    [cart, initialCartPromise]
  );

  const showSuccessNotification = useCallback(
    ({ product, variant, additionalProducts, gwp, checkoutUrl }) => {
      addToast(
        <AddToCartToastBody
          product={product}
          variant={variant}
          additionalProducts={additionalProducts}
          gwp={gwp}
          image={variant?.image}
          checkoutUrl={checkoutUrl}
        />,
        {
          type: 'positive',
          timer: 4000
        }
      );
    },
    [addToast]
  );

  const showErrorNotification = useCallback(
    (error) => {
      addToast(<ErrorAddToCartToastBody error={error} />, {
        type: 'error',
        timer: 4000
      });
    },
    [addToast]
  );

  // Manual bundle discount refresh function - for debugging or forced refresh
  const refreshBundleDiscounts = useCallback(async () => {
    if (!cart?.id || !cart?.lines?.length) {
      return { success: false, message: 'No cart available to refresh' };
    }

    try {
      const validation = await validateAndRefreshBundleDiscounts(cart);

      if (validation.needsRefresh && validation.fixedItems.length > 0) {
        const updatedCart = await updateCartAPI(cart.id, validation.fixedItems);

        if (updatedCart && !updatedCart.error) {
          setCart(updatedCart);
          return {
            success: true,
            message: `Updated ${validation.itemCount} bundle discount(s) with fresh metafield data`,
            itemCount: validation.itemCount
          };
        } else {
          return {
            success: false,
            message: 'Failed to update cart with fixed discounts'
          };
        }
      } else {
        return {
          success: true,
          message: 'All bundle discounts are already current'
        };
      }
    } catch (error) {
      if (isBotDeniedError(error)) {
        console.warn(BOT_BLOCKED_WARNING, error);
      } else {
        console.error('Error refreshing bundle discounts:', error);
      }
      return {
        success: false,
        message: 'Error occurred while refreshing bundle discounts'
      };
    }
  }, [cart, setCart]);

  // Deletes item optimistically
  const deleteCartItem = useCallback(
    async (merchandiseIdArray) => {
      const handleError = (err) => {
        if (isBotDeniedError(err)) {
          console.warn(BOT_BLOCKED_WARNING, err);
          return { cart: null, error: err };
        }
        if (err) {
          console.error('Failed to delete item from cart:', err);
        }
        const error = {
          message:
            'Something went wrong while removing the item from your cart. Please try again later or chat with our Comfort Crew for assistance.'
        };
        showErrorNotification(error);
        return { cart: null, error };
      };

      if (!cart?.id || !merchandiseIdArray?.length) {
        return handleError();
      }

      // Ensure both optimistic and real cart are updated nested within the
      // same startTransition to avoid flickering.
      startTransition(async () => {
        updateOptimisticCart({
          type: 'DELETE',
          payload: { checkoutId: cart.id, merchandiseIdArray }
        });

        try {
          const newCart = await removeFromCartAPI(cart.id, merchandiseIdArray);
          startTransition(() => {
            setCart(newCart);
          });
        } catch (err) {
          return handleError(err);
        }
      });
    },
    [cart, setCart, updateOptimisticCart, showErrorNotification]
  );

  // Updates cart quantity
  const updateCartItem = useCallback(
    async (items, updateType) => {
      const handleError = (err) => {
        if (isBotDeniedError(err)) {
          console.warn(BOT_BLOCKED_WARNING, err);
          return { cart: null, error: err };
        }
        if (err) {
          console.error('Failed to update item from cart:', err);
        }
        const error = {
          message:
            'Your items were not updated due to an unexpected error. Please try again in a minute or chat with our Comfort Crew to place your order.'
        };
        showErrorNotification(error);
        return { cart: null, error };
      };

      if (!cart?.id) {
        return handleError();
      }

      const updatedItems = [];
      const itemsToDelete = [];

      items.forEach(({ id }) => {
        // Unlike the other operations, we allow quantity changes to reference
        // the optimistic cart directly for even faster updates.
        const existingItem = optimisticCart?.lines?.find(
          (item) => item.id === id
        );

        if (existingItem) {
          const updatedQuantity =
            updateType === 'plus'
              ? existingItem.quantity + 1
              : existingItem.quantity - 1;

          if (updatedQuantity === 0) {
            itemsToDelete.push(id);
          } else {
            updatedItems.push({
              id: existingItem.id,
              quantity: updatedQuantity
            });
          }
        }
      });

      // No operation needed
      if (!updatedItems.length && !itemsToDelete.length) {
        return;
      }

      startTransition(async () => {
        updatedItems.forEach(({ id, quantity }) => {
          updateOptimisticCart({
            type: 'UPDATE_ITEM',
            payload: { merchandiseId: id, updatedQuantity: quantity }
          });
        });

        if (itemsToDelete.length) {
          deleteCartItem(itemsToDelete);
        }

        try {
          if (updatedItems.length) {
            const newCart = await updateCartAPI(cart.id, updatedItems);
            startTransition(() => {
              setCart(newCart);
            });
          }
        } catch (err) {
          return handleError(err);
        }
      });
    },
    [
      cart,
      optimisticCart,
      setCart,
      updateOptimisticCart,
      deleteCartItem,
      showErrorNotification
    ]
  );

  const addCartItems = useCallback(
    async (items, options = {}) => {
      const { suppressSuccessToast = false } = options;

      const handleError = (err) => {
        if (isBotDeniedError(err)) {
          console.warn(BOT_BLOCKED_WARNING, err);
          return { cart: null, error: err };
        }
        if (err) {
          console.error('Failed to add item from cart:', err);
        }
        const error = {
          message:
            'Your items were not added to the cart due to an unexpected error. Please try again in a minute or chat with our Comfort Crew to place your order.'
        };
        showErrorNotification(error);
        return { cart: null, error };
      };

      if (!items?.length) {
        return handleError();
      }

      const itemsToAdd = items.map((item) => ({
        merchandiseId: item.variant.id,
        quantity: item.quantity ?? 1,
        attributes: item.customAttributes || []
      }));

      // We need to keep a reference to a deferred promise because the
      // startTransition callback doesn't return a value to us here.
      const deferred = createDeferred();

      // Ensure both optimistic and real cart are updated nested within the
      // same startTransition to avoid flickering.
      startTransition(async () => {
        try {
          updateOptimisticCart({ type: 'ADD_MULTIPLE_ITEMS', payload: items });

          // Creates the cart on demand if one doesn't exist yet, or adds to
          // the existing one.
          const newCart = await addOrCreateCart(itemsToAdd);
          // Check if we need to validate bundle discounts for the updated cart
          let finalCart = newCart;
          if (newCart && newCart.lines && newCart.lines.length > 0) {
            const bundleItems = newCart.lines.filter((item) =>
              item.attributes?.some(
                (attr) =>
                  attr.key === '_bundleHandle' ||
                  attr.key.includes('bundleDiscountAmount')
              )
            );
            if (bundleItems.length > 0) {
              try {
                const validation =
                  await validateAndRefreshBundleDiscounts(newCart);

                if (
                  validation.needsRefresh &&
                  validation.fixedItems.length > 0
                ) {
                  const updatedCart = await updateCartAPI(
                    newCart.id,
                    validation.fixedItems
                  );
                  if (updatedCart && !updatedCart.error) {
                    finalCart = {
                      ...updatedCart,
                      _bundleValidated: Date.now()
                    };
                  }
                }
              } catch (bundleError) {
                if (isBotDeniedError(bundleError)) {
                  console.warn(BOT_BLOCKED_WARNING, bundleError);
                } else {
                  console.error(
                    'Error validating bundle discounts after add:',
                    bundleError
                  );
                }
              }
            }
          }
          startTransition(() => {
            setCart(finalCart);
          });

          // Set success notification with item details
          if (!suppressSuccessToast) {
            const firstItem = items[0];
            const additionalProducts = items
              .slice(1)
              .filter(
                (item) =>
                  !item.customAttributes?.find((attr) => attr.key === '_isGwp')
              );
            const gwpItems = items.filter((item) =>
              item.customAttributes?.find((attr) => attr.key === '_isGwp')
            );

            showSuccessNotification({
              product: firstItem.product,
              variant: firstItem.variant,
              additionalProducts,
              gwp: gwpItems,
              checkoutUrl: finalCart?.checkoutUrl
            });
          }

          deferred.resolve({ cart: finalCart, error: null });
        } catch (err) {
          // Return with the error object for the caller.
          deferred.resolve(handleError(err));
        }
      });

      return deferred.promise;
    },
    [
      addOrCreateCart,
      setCart,
      updateOptimisticCart,
      showSuccessNotification,
      showErrorNotification
    ]
  );

  const value = useMemo(
    () => ({
      cart: optimisticCart,
      deleteCartItem,
      updateCartItem,
      addCartItems,
      refreshBundleDiscounts,
      cartReady
    }),
    [
      optimisticCart,
      deleteCartItem,
      updateCartItem,
      addCartItems,
      refreshBundleDiscounts,
      cartReady
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

/**
 * A helper to get the cart from the server, preferring the cart from context if
 * it exists. If you're on a block that happens to read the cart on the server,
 * use this rather than reading the cart off `useCart()` directly. It will help
 * tell React that hydration errors are OK sometimes and which cart to believe.
 * Specifically this helps our cypress tests where we intercept the cart requests
 * so they don't stop because we forced in an invalid cart structure.
 */
export function useCartToRender(cartFromServer) {
  const { cart: cartFromContext } = useCart();
  const hydrated = useHydrated();

  return hydrated ? (cartFromContext ?? cartFromServer) : cartFromServer;
}
